/** @file Creates a slot on a bone, drawn on top (appended to drawOrder and every draw-order key). */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { defineCommand } from '../defineCommand';
import { defineCreate } from '../_shared/defineCreate';
import { warnUnmappedPlaceholder } from './warnUnmappedPlaceholder';

const SETUP = { blend: 'normal', color: '#ffffffff', dark: null, attachment: null } as const;

const create = defineCreate({
  domain: 'slot',
  kind: 'slot',
  description: 'Create a slot on `bone` (name or id), drawn on top of every existing slot (appended to drawOrder and to every draw-order key); optional blend, color (#rrggbbaa), dark (#rrggbb two-color tint or null) and setup placeholder `attachment`.',
  schema: z
    .object({
      id: z.string().exactOptional(),
      name: z.string().min(1),
      bone: z.string().min(1),
      blend: prim.blend.exactOptional(),
      color: prim.hex8.exactOptional(),
      dark: prim.hex6.nullable().exactOptional(),
      attachment: prim.name.nullable().exactOptional(),
    })
    .strict(),
  build(input, ctx) {
    const s = { ...SETUP, ...input };
    return { name: s.name, bone: ctx.resolve('bone', s.bone), blend: s.blend, setup: { color: s.color, dark: s.dark, attachment: s.attachment } };
  },
});

/** slot.create: keeps setup drawOrder and every non-null draw-order key a full slot permutation. */
export const command = defineCommand({
  ...create,
  apply(draft, input, ctx) {
    create.apply(draft, input, ctx);
    const id = ctx.created[input.name] ?? '';
    draft.drawOrder.push(id);
    for (const anim of Object.values(draft.animations)) for (const key of anim.drawOrder) key.order?.push(id);
    if (typeof input.attachment === 'string') warnUnmappedPlaceholder(draft, id, input.attachment, ctx, 'attachment');
  },
});
